<template>
  <aside :class="['progress-sidebar', { collapsed }]">
    <!-- Toggle Button -->
    <button class="toggle-btn" @click="toggle"
            :title="collapsed ? 'Afficher' : 'Réduire'">
      <span>{{ collapsed ? '‹' : '›' }}</span>
    </button>

    <!-- Content -->
    <div class="sidebar-content">
      <div class="sidebar-header">
        <h3>📊 {{ $t('dashboard.global_progress') }}</h3>
      </div>

      <div class="progress-items">
        <div v-for="project in projects" :key="project.id" class="project-progress">
          <div class="project-name">{{ project.name }}</div>
          <div class="progress-info">
            <span class="progress-label">
              {{ project.checkpoints_passed }}/{{ project.checkpoints_total }}
            </span>
            <span class="progress-percent">
              {{ Math.round((project.checkpoints_passed / project.checkpoints_total) * 100) }}%
            </span>
          </div>
          <div class="progress-bar">
            <div class="progress-fill" :style="{ width: (project.checkpoints_passed / project.checkpoints_total) * 100 + '%', background: getProgressColor(project.checkpoints_passed, project.checkpoints_total) }"></div>
          </div>
          <div class="trust-score">
            <span class="score-label">Score:</span>
            <span class="score-value" :class="getTrustScoreClass(project.ai_trust_score)">
              {{ project.ai_trust_score }}%
            </span>
          </div>
        </div>
      </div>

      <!-- Global Stats -->
      <div class="global-stats">
        <div class="stat-item">
          <span class="stat-label">{{ $t('dashboard.total_projects') }}</span>
          <span class="stat-value">{{ projects.length }}</span>
        </div>
        <div class="stat-item">
          <span class="stat-label">{{ $t('dashboard.avg_progress') }}</span>
          <span class="stat-value">{{ avgProgress }}%</span>
        </div>
        <div class="stat-item">
          <span class="stat-label">{{ $t('dashboard.avg_trust_score') }}</span>
          <span class="stat-value">{{ avgTrustScore }}%</span>
        </div>
      </div>
    </div>
  </aside>
</template>

<script setup>
import { computed, onMounted } from 'vue'
import { useProjectStore } from '../stores/projectStore'

const props = defineProps({
  collapsed: { type: Boolean, default: false }
})
const emit = defineEmits(['update:collapsed'])

const collapsed = computed(() => props.collapsed)
const toggle = () => emit('update:collapsed', !props.collapsed)

const projectStore = useProjectStore()

const projects = computed(() => {
  // Filter by active status and current user
  const userProjects = projectStore.userProjects
  return userProjects.filter(p => p.status !== 'archived' && p.status !== 'completed')
})

const avgProgress = computed(() => {
  if (projects.value.length === 0) return 0
  const total = projects.value.reduce((sum, p) => sum + (p.checkpoints_passed / p.checkpoints_total), 0)
  return Math.round((total / projects.value.length) * 100)
})

const avgTrustScore = computed(() => {
  if (projects.value.length === 0) return 0
  const total = projects.value.reduce((sum, p) => sum + p.ai_trust_score, 0)
  return Math.round(total / projects.value.length)
})

const getProgressColor = (passed, total) => {
  const percent = (passed / total) * 100
  if (percent >= 75) return 'linear-gradient(90deg, #10b981, #059669)'
  if (percent >= 50) return 'linear-gradient(90deg, #f59e0b, #d97706)'
  return 'linear-gradient(90deg, #ef4444, #dc2626)'
}

const getTrustScoreClass = (score) => {
  if (score >= 85) return 'score-excellent'
  if (score >= 70) return 'score-good'
  if (score >= 50) return 'score-fair'
  return 'score-poor'
}

onMounted(async () => {
  await projectStore.getProjects()
})
</script>

<style scoped>
.progress-sidebar {
  width: 280px;
  height: 100vh;
  background: white;
  border-left: 1px solid var(--color-border);
  position: fixed;
  right: 0;
  top: 0;
  display: flex;
  flex-direction: column;
  z-index: 40;
  box-shadow: -2px 0 12px rgba(0, 0, 0, 0.08);
  transition: width 0.25s cubic-bezier(.4, 0, .2, 1);
  overflow: hidden;
}

.progress-sidebar.collapsed {
  width: 0;
  border-left: none;
  box-shadow: none;
}

/* Toggle button is fixed to the viewport so it is never clipped
   by the sidebar overflow and stays reachable when collapsed. */
.toggle-btn {
  position: fixed;
  top: 1rem;
  right: 258px;
  width: 44px;
  height: 44px;
  border-radius: 50%;
  background: #3b82f6;
  color: #fff;
  font-size: 1.4rem;
  font-weight: 700;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 60;
  box-shadow: 0 6px 16px rgba(59, 130, 246, .6);
  transition: right 0.25s cubic-bezier(.4, 0, .2, 1), background 0.2s, transform 0.2s;
  padding: 0;
  line-height: 1;
  border: none;
  cursor: pointer;
}

.progress-sidebar.collapsed .toggle-btn {
  right: 12px;
}

.toggle-btn:hover {
  background: #2563eb;
  transform: scale(1.12);
}

.sidebar-content {
  flex: 1;
  overflow-y: auto;
  padding: 2rem 1.5rem;
}

.sidebar-header {
  margin-bottom: 1.5rem;
  padding-bottom: 1rem;
  border-bottom: 2px solid var(--color-border);
}

.sidebar-header h3 {
  margin: 0;
  font-size: 1rem;
  color: var(--color-text);
  font-weight: 600;
}

.progress-items {
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
  margin-bottom: 2rem;
}

.project-progress {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.project-name {
  font-size: 0.85rem;
  font-weight: 600;
  color: var(--color-text);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.progress-info {
  display: flex;
  justify-content: space-between;
  font-size: 0.75rem;
  color: var(--color-gray-600);
}

.progress-percent {
  font-weight: 600;
  color: var(--color-primary);
}

.progress-bar {
  height: 6px;
  background: var(--color-gray-200);
  border-radius: 10px;
  overflow: hidden;
}

.progress-fill {
  height: 100%;
  border-radius: 10px;
  transition: width 0.3s ease;
}

.trust-score {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-top: 0.25rem;
}

.score-label {
  font-size: 0.7rem;
  color: var(--color-gray-600);
  font-weight: 600;
}

.score-value {
  font-size: 0.8rem;
  font-weight: 700;
  padding: 0.2rem 0.4rem;
  border-radius: var(--radius-sm);
}

.score-excellent {
  background: #dcfce7;
  color: #166534;
}

.score-good {
  background: #fef3c7;
  color: #92400e;
}

.score-fair {
  background: #fed7aa;
  color: #7c2d12;
}

.score-poor {
  background: #fee2e2;
  color: #991b1b;
}

.global-stats {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  padding: 1rem;
  background: var(--color-gray-50);
  border-radius: var(--radius-md);
  border: 1px solid var(--color-border);
}

.stat-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: 0.85rem;
}

.stat-label {
  color: var(--color-gray-600);
  font-weight: 500;
}

.stat-value {
  font-weight: 700;
  color: var(--color-primary);
  font-size: 1rem;
}

/* The supplementary progress rail only docks on large screens (≥1280px).
   Below that the central content takes priority and the rail is hidden. */
@media (max-width: 1279px) {
  .progress-sidebar,
  .progress-sidebar.collapsed {
    width: 0;
    border-left: none;
    box-shadow: none;
  }

  .toggle-btn,
  .progress-sidebar.collapsed .toggle-btn {
    display: none;
  }
}

::-webkit-scrollbar {
  width: 6px;
}

::-webkit-scrollbar-track {
  background: var(--color-gray-50);
}

::-webkit-scrollbar-thumb {
  background: var(--color-gray-300);
  border-radius: 3px;
}

::-webkit-scrollbar-thumb:hover {
  background: var(--color-gray-400);
}
</style>
